import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import type { Nurse, NurseInShift } from "../../utils/models";
import type { ShiftWindow } from "../../utils/scheduleConflict";
import { QUICK_ADD_SOURCE } from "./quickAddConsts";
import { useQuickAddWarnings } from "./useQuickAddWarnings";

/** What a click on the primary committed to: moving that shift, or adding a new one. */
type SaveDecision = { kind: "move"; existing: NurseInShift } | { kind: "add" };

type Params = {
	nurse: Nurse | null;
	location: string | null;
	window: ShiftWindow;
	movingShift: NurseInShift | undefined;
	weekIndex: number;
	dayIndex: number;
	showActual: boolean;
	performAdd: () => Promise<void>;
	performMove: (existing: NurseInShift) => Promise<void>;
};

/**
 * The "+" dialog's primary: decide move-or-add AT THE CLICK, run the warning cascade, and carry out that same
 * decision once every warning is confirmed. Re-deciding at confirmation would act on whatever the schedule says
 * by then — a refresh landing while a warning is open could turn the confirmed move into an add. The cascade
 * calls its completion without awaiting it, so a rejection is caught here and shown, never left unhandled.
 */
export const useQuickAddSave = ({ performAdd, performMove, ...warningParams }: Params) => {
	const { t } = useTranslation();
	const { showErrorSnackbar } = useHospitalData();
	const decisionRef = useRef<SaveDecision | null>(null);

	const complete = useCallback(() => {
		const decision = decisionRef.current;
		decisionRef.current = null;
		const context = { nurse_id: warningParams.nurse?.id, source: QUICK_ADD_SOURCE };
		if (!decision) {
			showErrorSnackbar(t("error.add.nurse"), { ...context, reason: "completed_without_save_click" });
			return;
		}
		const run = decision.kind === "move" ? performMove(decision.existing) : performAdd();
		run.catch((error: unknown) => showErrorSnackbar(t(decision.kind === "move" ? "error.move.nurse" : "error.add.nurse"), context, error));
	}, [warningParams.nurse?.id, showErrorSnackbar, t, performMove, performAdd]);

	const { handleSave: runWarnings, warningDialogProps } = useQuickAddWarnings({ ...warningParams, onComplete: complete });
	const { movingShift } = warningParams;

	const handleSave = useCallback(() => {
		decisionRef.current = movingShift ? { kind: "move", existing: movingShift } : { kind: "add" };
		runWarnings();
	}, [movingShift, runWarnings]);

	return { handleSave, warningDialogProps };
};
